import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Route, Loader2, Code2, Server, BarChart3, BookOpen, PenLine, Image as ImageIcon, Video, Info, Sparkles, Briefcase, MapPin } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { api } from "../lib/api";

const ICONS = { coding: Code2, it: Server, analytics: BarChart3, research: BookOpen, writing: PenLine, business: Briefcase, google: MapPin, image: ImageIcon, video: Video };

function RecommendTip({ c }) {
  if (!c.recommendation) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700" aria-label="Alasan rekomendasi" data-testid={`agent-recommend-tip-${c.id}`}>
            <Info size={13} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed" data-testid={`agent-recommend-text-${c.id}`}>
          <span className="block font-semibold">Rekomendasi platform{c.default_model_label ? `: ${c.default_model_label}` : ""}</span>
          {c.recommendation}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function autoLabel(c, o) {
  if (o.id !== "auto") return o.label;
  return c.default_model_label ? `Rekomendasi platform: ${c.default_model_label}` : o.label;
}

// Profile → Pengaturan Agen: the model each specialist Agent uses ("auto" = the platform-recommended default model of that Agent).
// With `personaId` it edits that assistant's own routes (Persona → Pengaturan Agen tab): defaults follow the user's Settings
// routes ("inherited") until the user changes & saves them here; "Ikuti Settings" drops the override again.
export function ModelRoutesCard({ personaId = null }) {
  const url = personaId ? `/personas/${personaId}/model-routes` : "/me/model-routes";
  const [cfg, setCfg] = useState(null);
  const [routes, setRoutes] = useState({});
  const enabled = true; // every Agent is always active; only the model behind it is configurable
  const [busy, setBusy] = useState(false);
  const apply = (d) => { setCfg(d); setRoutes(d.routes); };
  useEffect(() => { api.get(url).then((r) => apply(r.data)).catch(() => toast.error("Gagal memuat pengaturan agen")); }, [url]);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(url, { enabled, routes }); apply(r.data); toast.success("Pengaturan agen tersimpan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan agen"); } finally { setBusy(false); }
  };
  const inherit = async () => {
    setBusy(true);
    try { const r = await api.put(url, { inherit: true }); apply(r.data); toast.success("Asisten kembali mengikuti Pengaturan Agen di Settings"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah pengaturan agen"); } finally { setBusy(false); }
  };
  const reset = () => setRoutes(Object.fromEntries(Object.keys(routes).map((k) => [k, "auto"])));
  if (!cfg) return <div className="mt-4 aivora-card p-6 text-sm text-slate-500" data-testid="model-routes-loading"><Loader2 size={14} className="inline animate-spin" /> Memuat…</div>;
  const pinned = Object.values(routes).filter((v) => v && v !== "auto").length;
  return (
    <div className={personaId ? "" : "mt-4 aivora-card p-6"} data-testid="model-routes-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-semibold text-slate-900" data-testid="agent-settings-title"><Route size={16} className="text-[#2F6BFF]" /> Pengaturan Agen</p>
          <p className="mt-1 text-sm text-slate-500">{personaId
            ? "Otak asisten ini selalu GPT Luna. Untuk tiap Agen, Luna mendelegasikan ke model yang Anda tetapkan di sini. Setiap Agen punya model rekomendasi platform — arahkan ke ikon ⓘ untuk alasannya."
            : "Tentukan model untuk setiap Agen. Asisten tetap sama; pekerjaan didelegasikan ke Agen sesuai jenis permintaan. Setiap Agen punya model rekomendasi platform — arahkan ke ikon ⓘ untuk alasannya."}</p>
          {personaId && <p className="mt-1.5 text-xs font-medium text-slate-500" data-testid="model-routes-source">{cfg.inherited ? "Saat ini mengikuti Pengaturan Agen di Settings — ubah lalu simpan untuk menetapkan pengaturan khusus asisten ini." : "Pengaturan khusus asisten ini (menimpa Settings)."}</p>}
        </div>
      </div>
      <div className={`mt-4 grid gap-3 sm:grid-cols-2 ${enabled ? "" : "opacity-50"}`}>
        {cfg.categories.map((c) => { const Icon = ICONS[c.id] || (c.custom ? Sparkles : Route); return (
          <div key={c.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`model-route-${c.id}`}>
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Icon size={14} className="text-[#7C3AED]" /> {c.label}<RecommendTip c={c} /></p>
            <p className="mt-0.5 text-xs text-slate-500">{c.desc}</p>
            <select className="input-dark mt-2 w-full py-1.5 text-sm" value={routes[c.id] || "auto"} disabled={!enabled} onChange={(e) => setRoutes((r) => ({ ...r, [c.id]: e.target.value }))} data-testid={`model-route-select-${c.id}`}>
              {(cfg.options[c.kind] || []).map((o) => <option key={o.id} value={o.id}>{autoLabel(c, o)}</option>)}
            </select>
          </div>
        ); })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy} className="btn-grad rounded-xl px-5 py-2.5 text-sm" data-testid="model-routes-save">{busy ? "Menyimpan..." : "Simpan pengaturan agen"}</button>
        <button onClick={reset} className="rounded-xl px-3 py-2 text-sm text-slate-500 hover:text-slate-800" data-testid="model-routes-reset">Kembalikan ke rekomendasi</button>
        {personaId && !cfg.inherited && <button onClick={inherit} disabled={busy} className="rounded-xl px-3 py-2 text-sm text-slate-500 hover:text-slate-800" data-testid="model-routes-inherit">Ikuti Settings</button>}
        <span className="ml-auto text-xs text-slate-500" data-testid="model-routes-pinned">{`${pinned} Agen ditetapkan`}</span>
      </div>
    </div>
  );
}
